import React, { Suspense, lazy, useRef, useState } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { useIsMobile, usePrefersReducedMotion, hasWebGL } from '../hooks/useIsMobile';
import { useInViewActive } from '../hooks/useInViewActive';
import { HERO_JARS } from './three/jarMeta';
import { SceneBoundary } from './SceneBoundary';
import { COLORS } from '../constants';

const HeroScene = lazy(() => import('./three/HeroScene'));

const HeroFallback = () => (
  <div className="hero-fallback" data-testid="hero-fallback">
    <img src="/images/kit-cutout.webp" alt="BeautyKit Antiossidante Tensore" className="hero-fallback-img animate-float" />
  </div>
);

const fade = (delay) => ({
  initial: { opacity: 0, y: 28 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] },
});

const Hero3D = ({ onMix, onCommunity }) => {
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const reduced = usePrefersReducedMotion();
  const [hovered, setHovered] = useState(null);
  const [webgl] = useState(() => hasWebGL());
  const wrapRef = useRef();
  const active = useInViewActive(wrapRef);
  const scrollFx = webgl && !reduced;

  const { scrollYProgress } = useScroll({ target: wrapRef, offset: ['start start', 'end end'] });
  const textOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const textY = useTransform(scrollYProgress, [0, 0.3], [0, -40]);
  const captionOpacity = useTransform(scrollYProgress, [0.4, 0.65, 1], [0, 1, 1]);
  const captionY = useTransform(scrollYProgress, [0.4, 0.65], [30, 0]);
  const scrollHintOpacity = useTransform(scrollYProgress, [0, 0.12], [1, 0]);

  const jar = hovered !== null ? HERO_JARS[hovered] : null;

  return (
    <div ref={wrapRef} className={`hero3d-wrap ${scrollFx ? '' : 'no-scroll-fx'}`} data-testid="hero-section">
      <section className="hero3d">
        <motion.div className="hero3d-content" style={scrollFx ? { opacity: textOpacity, y: textY } : undefined}>
          <motion.span {...fade(0.05)} className="hero-badge" data-testid="hero-badge">
            <span className="hero-badge-dots">
              {Object.values(COLORS).map((c) => <i key={c} style={{ background: c }} />)}
            </span>
            {t('hero.badge')}
          </motion.span>
          <motion.h1 {...fade(0.15)} className="hero-title text-gradient-brand" data-testid="hero-title">
            {t('hero.title')}
          </motion.h1>
          <motion.p {...fade(0.28)} className="hero-subtitle" data-testid="hero-subtitle">
            {t('hero.subtitle')}
          </motion.p>
          <motion.div {...fade(0.4)} className="hero-ctas">
            <button onClick={onMix} className="btn-pill btn-pill-primary" data-testid="hero-cta-mix">
              {t('hero.cta_mix')}
            </button>
            <button onClick={onCommunity} className="btn-pill btn-pill-ghost" data-testid="hero-cta-community">
              {t('hero.cta_community')}
            </button>
          </motion.div>
          <motion.p {...fade(0.6)} className="hero-hint" data-testid="hero-hint">
            {isMobile ? t('hero.hint_mobile') : t('hero.hint')}
          </motion.p>
        </motion.div>

        <div className="hero3d-stage">
          {scrollFx ? (
            <SceneBoundary fallback={<HeroFallback />}>
              <Suspense fallback={<HeroFallback />}>
                <HeroScene isMobile={isMobile} active={active} progress={scrollYProgress} onHover={setHovered} onClick={onMix} />
              </Suspense>
            </SceneBoundary>
          ) : (
            <HeroFallback />
          )}

          {scrollFx && (
            <motion.p className="hero-caption" style={{ opacity: captionOpacity, y: captionY, x: '-50%' }} data-testid="hero-scroll-caption">
              {t('hero.scroll_caption')}
            </motion.p>
          )}

          <AnimatePresence>
            {jar && (
              <motion.div
                key={hovered}
                initial={{ opacity: 0, y: 12, x: '-50%', scale: 0.95 }}
                animate={{ opacity: 1, y: 0, x: '-50%', scale: 1 }}
                exit={{ opacity: 0, y: 8, x: '-50%', scale: 0.97 }}
                transition={{ duration: 0.22 }}
                className="jar-tooltip"
                style={{ '--line': COLORS[jar.color] }}
                data-testid="jar-tooltip"
              >
                <span className="jar-tooltip-dot" />
                {t(jar.nameKey)} · 10 ml · €3.90
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <motion.div className="hero-scroll" style={scrollFx ? { opacity: scrollHintOpacity } : undefined} aria-hidden>
          <span>{t('hero.scroll')}</span>
          <i />
        </motion.div>
      </section>
    </div>
  );
};

export default Hero3D;
